import { Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';

import { BarChart } from '../charts/BarChart';
import { MetricTileHelper } from '../helpers/MetricTileHelper';
import { PeriodHelper } from '../helpers/PeriodHelper';
import { ReportFormatHelper } from '../helpers/ReportFormatHelper';
import type { IComponentFacts, MetricTileId } from '../types/report';
import { moneyFormatter } from './charts/chartFormatters';

interface IMetricTileProps {
  metric: MetricTileId;
  facts: IComponentFacts;
}

const TREND_CATEGORY = 'Trend';

const DeltaIcon = ({ delta }: { delta: number | null }) => {
  if (delta === null) return <Icons.Minus className="size-3" />;
  return delta > 0 ? (
    <Icons.ArrowUpRight className="size-3.5" />
  ) : (
    <Icons.ArrowDownRight className="size-3.5" />
  );
};

export const MetricTile = ({ metric, facts }: IMetricTileProps) => {
  const { current, comparison, currency, resolved, table } = facts;
  const spec = MetricTileHelper.spec(metric);
  const value = spec.value(current);
  const { delta, pct, favourable } = MetricTileHelper.delta(
    spec,
    value,
    comparison ? spec.value(comparison) : null
  );
  const secondary = spec.secondary?.(current, currency) ?? null;
  const trend = MetricTileHelper.trend(
    metric,
    table,
    PeriodHelper.trendMonths(resolved),
    TREND_CATEGORY
  );
  const trendFormatter =
    spec.format === 'months'
      ? (v: number) => ReportFormatHelper.months(v)
      : moneyFormatter(currency);

  return (
    <div className="flex h-full flex-col gap-md">
      <div className="flex items-start justify-between gap-md">
        <div className="min-w-0">
          <p className="text-2xl font-semibold tracking-tight tabular-nums text-text-primary">
            {MetricTileHelper.formatValue(spec, value, currency)}
          </p>
          {secondary && (
            <p className="txt-extra-small-regular text-text-tertiary">
              {secondary}
            </p>
          )}
        </div>
        <div
          className={cn(
            'flex shrink-0 items-center gap-xs txt-extra-small-regular tabular-nums',
            favourable === null && 'text-text-tertiary',
            favourable === true && 'text-text-success',
            favourable === false && 'text-text-error'
          )}
        >
          <DeltaIcon delta={delta} />
          <span>{MetricTileHelper.formatDelta(spec, delta, currency)}</span>
          {pct !== null && (
            <span className="text-text-tertiary">
              ({ReportFormatHelper.percent(pct, 1, true)})
            </span>
          )}
        </div>
      </div>
      <BarChart
        className="h-auto min-h-0 flex-1"
        data={trend}
        index="label"
        categories={[TREND_CATEGORY]}
        colors={[favourable === false ? 'rose' : 'blue']}
        valueFormatter={trendFormatter}
        showYAxis={false}
        showLegend={false}
      />
    </div>
  );
};
